/**
 * Frontend half of the `media_kind` vocabulary.
 *
 * The backend owns these values in `backend/app/services/media_kind.py`
 * (`MediaKind`): ingest classifies YouTube videos and PDFs — targets our
 * extractor can't read — and read paths surface `media_kind` so clients mark
 * them next to the title, the way `is_gated` renders ($). The union used to
 * be spelled out inline in four `types.ts` declarations, and the marker span
 * itself was hand-copied three times across StoryRow and StoryDetail. This
 * module owns the vocabulary and the marker text; `<MediaKindMarker>` owns
 * the JSX. `backend/tests/test_mirror_parity.py` pins the two stacks
 * together.
 */
export const MEDIA_KINDS = ['youtube', 'pdf'] as const;

export type MediaKind = (typeof MEDIA_KINDS)[number];

export interface MediaKindMarkerText {
  label: string;
  title: string;
}

const MEDIA_KIND_MARKERS: Record<MediaKind, MediaKindMarkerText> = {
  youtube: { label: '(YouTube)', title: 'YouTube video' },
  pdf: { label: '(PDF)', title: 'PDF document' },
};

function isMediaKind(value: string): value is MediaKind {
  return (MEDIA_KINDS as readonly string[]).includes(value);
}

/** The marker text for a row's `media_kind`, or null when there is nothing to mark. */
export function mediaKindMarker(kind: string | null | undefined): MediaKindMarkerText | null {
  return kind && isMediaKind(kind) ? MEDIA_KIND_MARKERS[kind] : null;
}
